import React, { useState, useEffect, useRef } from 'react';
import { SlidersIcon, XIcon, SearchIcon } from './icons/InterfaceIcons';

interface FilterModalProps {
  subjects: string[];
  chapters: string[];
  activeSubject: string;
  activeChapter: string;
  activeDifficulty: string;
  onSubjectChange: (subject: string) => void;
  onChapterChange: (chapter: string) => void;
  onDifficultyChange: (difficulty: string) => void;
  questionCount: number;
  totalQuestions: number;
  onClose: () => void;
}

export const FloatingFilterModal: React.FC<FilterModalProps> = ({
  subjects,
  chapters,
  activeSubject,
  activeChapter,
  activeDifficulty,
  onSubjectChange,
  onChapterChange,
  onDifficultyChange,
  questionCount,
  totalQuestions,
  onClose
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [subjectSearch, setSubjectSearch] = useState('');
  const [chapterSearch, setChapterSearch] = useState('');
  const modalRef = useRef<HTMLDivElement>(null);

  const difficulties = ['All', 'Easy', 'Medium', 'Hard'];
  const hasActiveFilters = activeSubject !== 'All' || activeChapter !== 'All' || activeDifficulty !== 'All';

  const filteredSubjects = subjects.filter(subject =>
    subject.toLowerCase().includes(subjectSearch.toLowerCase())
  );

  const filteredChapters = chapters.filter(chapter =>
    chapter.toLowerCase().includes(chapterSearch.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const clearAllFilters = () => {
    onSubjectChange('All');
    onChapterChange('All');
    onDifficultyChange('All');
    setSubjectSearch('');
    setChapterSearch('');
  };

  const DifficultyButton: React.FC<{ difficulty: string }> = ({ difficulty }) => {
    const isActive = activeDifficulty === difficulty;
    const getDifficultyColor = (diff: string) => {
      switch (diff) {
        case 'Easy': return 'text-green-600 border-green-200 bg-green-50 dark:text-green-400 dark:border-green-800 dark:bg-green-900/20';
        case 'Medium': return 'text-yellow-600 border-yellow-200 bg-yellow-50 dark:text-yellow-400 dark:border-yellow-800 dark:bg-yellow-900/20';
        case 'Hard': return 'text-red-600 border-red-200 bg-red-50 dark:text-red-400 dark:border-red-800 dark:bg-red-900/20';
        default: return 'text-brand-text-primary border-brand-border bg-brand-surface';
      }
    };

    return (
      <button
        onClick={() => onDifficultyChange(difficulty)}
        className={`px-4 py-2 text-sm font-medium rounded-lg border transition-all duration-200 ${
          isActive
            ? 'border-brand-primary bg-brand-primary text-white shadow-md'
            : getDifficultyColor(difficulty)
        }`}
      >
        {difficulty}
      </button>
    );
  };

  return (
    <>
      {/* Floating Filter Button */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 right-6 z-[9999] w-14 h-14 rounded-full shadow-lg transition-all duration-300 hover:scale-110 ${
          hasActiveFilters 
            ? 'bg-brand-primary text-white' 
            : 'bg-brand-surface border-2 border-brand-border text-brand-text-primary hover:border-brand-primary'
        }`}
      >
        <SlidersIcon className="w-6 h-6 mx-auto" />
        {hasActiveFilters && (
          <span className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
            {[activeSubject !== 'All', activeChapter !== 'All', activeDifficulty !== 'All'].filter(Boolean).length}
          </span>
        )}
      </button>

      {/* Modal Overlay */}
      {isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-end justify-center bg-black/50 backdrop-blur-sm">
          <div 
            ref={modalRef}
            className="w-full max-w-lg bg-brand-surface rounded-t-2xl shadow-2xl transform transition-transform duration-300 ease-out max-h-[80vh] overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-brand-border">
              <div className="flex items-center space-x-3">
                <SlidersIcon className="w-5 h-5 text-brand-primary" />
                <h3 className="font-semibold text-brand-text-primary">Filter Questions</h3>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 hover:bg-brand-bg rounded-lg transition-colors"
              >
                <XIcon className="w-5 h-5 text-brand-text-secondary" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 space-y-6 overflow-y-auto max-h-[calc(80vh-80px)]">
              {/* Results Count */}
              <div className="text-center p-3 bg-brand-bg rounded-lg">
                <span className="text-sm font-medium text-brand-text-primary">
                  Showing {questionCount} of {totalQuestions} questions
                </span>
              </div>

              {/* Clear All */}
              {hasActiveFilters && (
                <button
                  onClick={clearAllFilters}
                  className="w-full p-3 text-sm font-medium text-brand-text-secondary hover:text-brand-primary border border-brand-border rounded-lg hover:bg-brand-bg transition-colors"
                >
                  Clear All Filters
                </button>
              )}

              {/* Difficulty Filter */}
              <div>
                <label className="block text-sm font-medium text-brand-text-primary mb-3">Difficulty</label>
                <div className="grid grid-cols-2 gap-2">
                  {difficulties.map(difficulty => (
                    <DifficultyButton key={difficulty} difficulty={difficulty} />
                  ))}
                </div>
              </div>

              {/* Subject Filter */}
              <div>
                <label className="block text-sm font-medium text-brand-text-primary mb-3">Subject</label>
                {subjects.length > 5 && (
                  <div className="relative mb-3">
                    <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-brand-text-secondary" />
                    <input
                      type="text"
                      placeholder="Search subjects..."
                      value={subjectSearch}
                      onChange={(e) => setSubjectSearch(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 bg-brand-bg border border-brand-border rounded-lg text-brand-text-primary placeholder-brand-text-secondary focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                    />
                  </div>
                )}
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {filteredSubjects.map(subject => (
                    <button
                      key={subject}
                      onClick={() => onSubjectChange(subject)}
                      className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                        activeSubject === subject
                          ? 'bg-brand-primary text-white'
                          : 'bg-brand-bg hover:bg-brand-primary/10 text-brand-text-primary'
                      }`}
                    >
                      {subject}
                    </button>
                  ))}
                </div>
              </div>

              {/* Chapter Filter */}
              {chapters.length > 1 && (
                <div>
                  <label className="block text-sm font-medium text-brand-text-primary mb-3">Chapter/Topic</label>
                  {chapters.length > 5 && (
                    <div className="relative mb-3">
                      <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-brand-text-secondary" />
                      <input
                        type="text"
                        placeholder="Search chapters..."
                        value={chapterSearch}
                        onChange={(e) => setChapterSearch(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 bg-brand-bg border border-brand-border rounded-lg text-brand-text-primary placeholder-brand-text-secondary focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                      />
                    </div>
                  )}
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {filteredChapters.map(chapter => (
                      <button
                        key={chapter}
                        onClick={() => onChapterChange(chapter)}
                        className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                          activeChapter === chapter
                            ? 'bg-brand-primary text-white'
                            : 'bg-brand-bg hover:bg-brand-primary/10 text-brand-text-primary'
                        }`}
                      >
                        {chapter}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};